/* eslint-disable camelcase */
import React, { useState } from "react"
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import ImageIcon from "@mui/icons-material/Image"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import CloseIcon from "@mui/icons-material/Close"
import { useQuery, api, showWarning } from "../../../../api"

// El componente recibe loccodigo y bodcodigo para el cálculo de stock
// Además recibe isDocBase para bloquear la grilla cuando se hereda una solicitud origen
const DetalleArticulosGrid = ({ detalle, setFormData, tsolInEg, clicodigo, loccodigo, bodcodigo, isDocBase }) => {
  // Estado para manejar el visualizador (Modal) de Imágenes y PDFs
  const [mediaViewer, setMediaViewer] = useState({ open: false, type: "", title: "", items: [] })

  // Estado individual de carga por cada fila cuando consulta el stock/multimedia
  const [loadingRow, setLoadingRow] = useState(null)

  // ==========================================================================
  // LÓGICA DINÁMICA DE COLUMNAS SEGÚN TIPO DE SOLICITUD
  // ==========================================================================
  const tipoSol = String(tsolInEg || "").toUpperCase()

  const filtroCliente = ["SOLINGGSGA", "SOLEGRGSGA"].includes(tipoSol)

  // Banderas independientes para Egresos y Recepciones
  const mostrarCantEgr =
    ["SOLEGRPRES", "SOLEGRBOD", "SOLEGRLOC", "SOLEGRGSGA", "SOLEGRAJU"].includes(tipoSol) || isDocBase
  const mostrarCantRec =
    ["SOLEGRPRES", "SOLEGRBOD", "SOLEGRLOC", "SOLINGGSGA", "SOLINGAJU"].includes(tipoSol) || isDocBase

  // Calculamos cuántas columnas extra se están mostrando para ajustar los anchos (widths) de la tabla
  const columnasExtra = (mostrarCantEgr ? 1 : 0) + (mostrarCantRec ? 1 : 0) + (isDocBase ? 1 : 0) // +1 si mostramos Cant. Pendiente
  const widthArticulo = columnasExtra >= 2 ? "20%" : columnasExtra === 1 ? "30%" : "45%"
  const widthStockCant = columnasExtra >= 2 ? "10%" : "15%"

  // ==========================================================================
  // EXTRACCIÓN DEFENSIVA DE ARTÍCULOS
  // ==========================================================================
  const { data: rawArticulos, isLoading: isLoadingArticulos } = useQuery({
    queryKey: ["listaArticulosDinamica", tsolInEg, clicodigo],
    queryFn: async () => {
      try {
        if (filtroCliente && !clicodigo) return []
        const payload = { filtro_cliente: filtroCliente, clicodigo: clicodigo || "" }
        const response = await api.post("/CrearSolicitudIngEgr/getArticulosFiltroDinamico", payload)
        const rawData = response?.data?.data || response?.data || response || []
        return Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : []
      } catch (error) {
        return []
      }
    },
    enabled: !!tsolInEg && !isDocBase, // Si es heredado, no cargamos catálogo masivo porque los artículos ya vienen incrustados
  })

  // Si heredamos de Documento Base, mapeamos el propio detalle como catálogo temporal para evitar errores de find() en el Autocomplete
  const listaArticulos = isDocBase
    ? detalle.map((item) => ({
        id: `${item.invcodigo}${item.artcodigo}`,
        label: `${item.artcodigo} - ${item.artdescri}`,
      }))
    : rawArticulos || []

  // ==========================================================================
  // MANEJADORES DE LA GRILLA
  // ==========================================================================
  const handleAddRow = () => {
    setFormData((prev) => ({
      ...prev,
      detalle: [
        ...prev.detalle,
        {
          idCompuesto: "",
          invcodigo: "",
          artcodigo: "",
          sgacansol: 0,
          sgacanegr: 0,
          sgacanrec: 0,
          stock: 0,
          imagenes: [],
          documentos_pdf: [],
        },
      ],
    }))
  }

  const handleRemoveRow = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      detalle: prev.detalle.filter((_, index) => index !== indexToRemove),
    }))
  }

  // ==========================================================================
  // EXTRACCIÓN ULTRA ROBUSTA PARA MULTIMEDIA Y STOCK
  // ==========================================================================
  const fetchMultimediaYStock = async (invcodigo, artcodigo, index) => {
    setLoadingRow(index)
    try {
      const payload = { invcodigo, artcodigo, loccodigo: loccodigo || "", bodcodigo: bodcodigo || "" }
      const res = await api.post("/CrearSolicitudIngEgr/getMultimediaYStockArticulo", payload)

      let details = { stock: 0, imagenes: [], documentos_pdf: [] }
      const r1 = res?.data

      if (r1?.data?.data?.imagenes || r1?.data?.data?.documentos_pdf || r1?.data?.data?.stock !== undefined) {
        details = r1.data.data
      } else if (r1?.data?.imagenes || r1?.data?.documentos_pdf || r1?.data?.stock !== undefined) {
        details = r1.data
      } else if (r1?.imagenes || r1?.documentos_pdf || r1?.stock !== undefined) {
        details = r1
      }

      setFormData((prev) => {
        const nuevoDetalle = [...prev.detalle]
        nuevoDetalle[index] = {
          ...nuevoDetalle[index],
          stock: details.stock || 0,
          imagenes: Array.isArray(details.imagenes) ? details.imagenes : [],
          documentos_pdf: Array.isArray(details.documentos_pdf) ? details.documentos_pdf : [],
        }
        return { ...prev, detalle: nuevoDetalle }
      })
    } catch (error) {
      console.error("Error obteniendo detalles multimedia:", error)
    } finally {
      setLoadingRow(null)
    }
  }

  const handleArticuloChange = (index, newValue) => {
    if (!newValue) {
      setFormData((prev) => {
        const nuevoDetalle = [...prev.detalle]
        nuevoDetalle[index] = {
          ...nuevoDetalle[index],
          idCompuesto: "",
          invcodigo: "",
          artcodigo: "",
          stock: 0,
          imagenes: [],
          documentos_pdf: [],
        }
        return { ...prev, detalle: nuevoDetalle }
      })
      return
    }

    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[index] = {
        ...nuevoDetalle[index],
        idCompuesto: String(newValue.id),
        invcodigo: newValue.invcodigo,
        artcodigo: newValue.artcodigo,
      }
      return { ...prev, detalle: nuevoDetalle }
    })

    fetchMultimediaYStock(newValue.invcodigo, newValue.artcodigo, index)
  }

  const handleCantidadChange = (index, value) => {
    let val = parseFloat(value) || 0
    if (val < 0) val = 0

    // Si está heredado, no puede solicitar devolver más de lo pendiente
    if (isDocBase && detalle[index]) {
      if (val > detalle[index].cant_pendiente) {
        showWarning(`No puede ingresar una cantidad mayor a la pendiente (${detalle[index].cant_pendiente}).`)
        val = detalle[index].cant_pendiente
      }
    }

    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[index] = { ...nuevoDetalle[index], sgacansol: val }
      return { ...prev, detalle: nuevoDetalle }
    })
  }

  // ==========================================================================
  // MANEJADOR DE MODALES DE VISUALIZACIÓN
  // ==========================================================================
  const openViewer = (type, title, items) => {
    setMediaViewer({ open: true, type, title, items })
  }

  const closeViewer = () => {
    setMediaViewer({ open: false, type: "", title: "", items: [] })
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        {/* Desactivamos agregar nuevas filas si heredamos de Documento Base */}
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddCircleIcon />}
          onClick={handleAddRow}
          disabled={(filtroCliente && !clicodigo) || isDocBase}
        >
          Agregar Artículo
        </Button>
      </Box>

      {filtroCliente && !clicodigo && !isDocBase && (
        <Box color="error.main" mb={2} fontSize="0.875rem">
          * Debe seleccionar un cliente primero para cargar el catálogo.
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f5f7fa" }}>
            <TableRow>
              <TableCell width="5%">#</TableCell>
              <TableCell width={widthArticulo}>Artículo (Código y Descripción)</TableCell>
              <TableCell width={widthStockCant} align="center">
                Stock Disp.
              </TableCell>

              {isDocBase && (
                <>
                  <TableCell width="10%" align="center">
                    Cant. Egr.
                  </TableCell>
                  <TableCell width="10%" align="center">
                    Cant. Rec.
                  </TableCell>
                  <TableCell width="10%" align="center">
                    Cant. Pend.
                  </TableCell>
                </>
              )}

              {/* Si es devolucion, etiquetamos como A Recibir, si no, Cantidad Solicitada */}
              <TableCell width={widthStockCant} align="center">
                {isDocBase ? "A Recibir" : "Cant. Sol."}
              </TableCell>

              {/* Mantenemos las columnas de auditoria históricas para flujos no docBase */}
              {!isDocBase && mostrarCantEgr && (
                <TableCell width="15%" align="center">
                  Cant. Egr.
                </TableCell>
              )}
              {!isDocBase && mostrarCantRec && (
                <TableCell width="15%" align="center">
                  Cant. Rec.
                </TableCell>
              )}

              <TableCell width="20%" align="center">
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detalle.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6 + columnasExtra} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No hay artículos en el detalle.
                </TableCell>
              </TableRow>
            ) : (
              detalle.map((row, index) => {
                // Cálculo para el Autocomplete condicional
                const idVirtual = isDocBase ? `${row.invcodigo}${row.artcodigo}` : row.idCompuesto

                return (
                  <TableRow
                    key={index}
                    sx={{ backgroundColor: isDocBase && row.cant_pendiente === 0 ? "#f0f0f0" : "inherit" }}
                  >
                    <TableCell>{index + 1}</TableCell>

                    <TableCell>
                      {/* Búsqueda bloqueada si es Documento Base */}
                      <Autocomplete
                        readOnly={row.locked}
                        options={listaArticulos}
                        getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                        value={
                          listaArticulos.find((a) => String(a.id) === String(idVirtual)) ||
                          (idVirtual
                            ? { id: idVirtual, label: `${row.artcodigo} - ${row.artdescri || "Cargando..."}` }
                            : null)
                        }
                        isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                        onChange={(event, newValue) => handleArticuloChange(index, newValue)}
                        loading={isLoadingArticulos && !row.locked}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={row.locked ? "" : "Buscar por código o descripción..."}
                            variant="standard"
                            InputProps={{ ...params.InputProps, disableUnderline: true }}
                          />
                        )}
                      />
                    </TableCell>

                    <TableCell align="center">
                      {loadingRow === index ? <CircularProgress size={20} /> : <b>{row.stock || 0}</b>}
                    </TableCell>

                    {/* COLUMNAS AUDITORÍA EN MODO DEVOLUCIÓN DOCUMENTO BASE */}
                    {isDocBase && (
                      <>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="textSecondary">
                            {row.cant_egresada || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {row.cant_ingresada || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="secondary">
                            {row.cant_pendiente || 0}
                          </Typography>
                        </TableCell>
                      </>
                    )}

                    <TableCell align="center">
                      {/* Campo interactivo para digitar la cantidad a recibir/solicitar */}
                      <TextField
                        type="number"
                        variant="outlined"
                        size="small"
                        inputProps={{ min: 0, step: "0.01" }}
                        value={row.sgacansol === 0 ? "" : row.sgacansol}
                        onChange={(e) => handleCantidadChange(index, e.target.value)}
                        disabled={isDocBase && row.cant_pendiente === 0}
                        fullWidth
                      />
                    </TableCell>

                    {/* COLUMNAS AUDITORÍA HISTÓRICAS PARA OTROS FLUJOS */}
                    {!isDocBase && mostrarCantEgr && (
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold" color="secondary">
                          {row.sgacanegr || 0}
                        </Typography>
                      </TableCell>
                    )}
                    {!isDocBase && mostrarCantRec && (
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {row.sgacanrec || 0}
                        </Typography>
                      </TableCell>
                    )}

                    <TableCell align="center">
                      <Tooltip title={row.imagenes?.length > 0 ? "Ver Imágenes" : "Sin Imágenes"}>
                        <span>
                          <IconButton
                            color="primary"
                            disabled={!row.imagenes || row.imagenes.length === 0}
                            onClick={() => openViewer("IMG", "Imágenes del Producto", row.imagenes)}
                          >
                            <ImageIcon />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title={row.documentos_pdf?.length > 0 ? "Ver PDFs" : "Sin Documentos PDF"}>
                        <span>
                          <IconButton
                            color="secondary"
                            disabled={!row.documentos_pdf || row.documentos_pdf.length === 0}
                            onClick={() => openViewer("PDF", "Documentos Técnicos PDF", row.documentos_pdf)}
                          >
                            <PictureAsPdfIcon />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {/* Botón Eliminar Fila desactivado si es heredado */}
                      <Tooltip title="Eliminar Fila">
                        <span>
                          <IconButton color="error" onClick={() => handleRemoveRow(index)} disabled={row.locked}>
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* =========================================================
          MODAL VISUALIZADOR DE MULTIMEDIA (IMÁGENES Y PDF)
      ========================================================= */}
      <Dialog open={mediaViewer.open} onClose={closeViewer} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          {mediaViewer.title}
          <IconButton color="inherit" onClick={closeViewer}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, backgroundColor: "#f4f6f8" }}>
          <Box display="flex" flexDirection="column" gap={3} alignItems="center">
            {mediaViewer.items.map((item, i) => (
              <Paper
                key={i}
                elevation={3}
                sx={{ p: 2, width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}
              >
                {mediaViewer.type === "IMG" ? (
                  <img
                    src={item.base64}
                    alt={`Imagen-${item.id}`}
                    style={{ maxWidth: "100%", maxHeight: "600px", objectFit: "contain" }}
                  />
                ) : (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                      {item.nombre}
                    </Typography>
                    <iframe
                      src={item.base64}
                      title={`PDF-${item.id}`}
                      width="100%"
                      height="600px"
                      style={{ border: "none" }}
                    />
                  </>
                )}
              </Paper>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default DetalleArticulosGrid
